import { memo, useCallback, useEffect, useMemo, useState } from "react";
import TradeOrdersContainer from "./tradeOrdersContainer/tradeOrdersContainer";
import { Box, Button, Divider, Grid, Stack, Typography } from "@mui/material";
import TradePanelControls from "./tradePanelControls/tradePanelControls";
import OrderBar from "./orderBar/orderBar";
import { selectBasketId, selectBatchId, selectStrategyBuilderMode, selectStrategyBuilderTradingAccountId, selectTradeOrders } from "../../selectors/strategyBuilder";
import { useSelector } from "react-redux";
import { checkForDuplicateTradeOrders } from "../../util/tradepanel";
import { setError, updateTradeOrders } from "../../reducer/strategyBuilder.reducer";
import { useDispatch } from "react-redux";
import TradePanelActionBar from "./tradePanelActionBar";
import { selectPrimaryTradingAccount, selectTradingAccounts, selectUserDetails } from "../../selectors/user";
import { selectBasketAnalyzeDetails, selectOrdersHistory } from '../../selectors/orders.ts'
import { setOpenStrategyTemplateDialog } from '../../reducer/strategyTemplates.reducer.ts'
import { Order, OrderStatus } from "../../proto/generated/order.ts";
import { selectPositions } from "../../selectors/positions.ts";
import TradeOrderAttributes from "./tradeOrderAttributes/tradeOrderAttributes.tsx";
import { useUser } from "../../context/UserContext.tsx";
import { FeatureCategory, FeatureName } from "../adminUserRestrictions/Restrictions.constants.ts";
import {
    getIcebergLegOrdersForSecurity,
    icebergLadderIncompleteForSecurity,
} from "../../util/icebergOrders.ts";

const TradePanel: React.FC = () => {
    const dispatch = useDispatch();
    const mode = useSelector(selectStrategyBuilderMode);
    const tradeOrders = useSelector(selectTradeOrders);
    const basketId = useSelector(selectBasketId);
    const batchId = useSelector(selectBatchId);
    const orderHistory = useSelector(selectOrdersHistory);
    const tradingAccounts = useSelector(selectTradingAccounts)
    const strategyTradingAccountId = useSelector(selectStrategyBuilderTradingAccountId);
    const selectedTradingAccount = useSelector(selectPrimaryTradingAccount);
    const basketAnalyzeDetails = useSelector(selectBasketAnalyzeDetails);
    const positions = useSelector(selectPositions);
    const {user: {hasAccess}} = useUser();
    const hasSlAndTargetAccess = hasAccess(FeatureCategory.TRADING , '' , FeatureName.SL_AND_TARGET)
    const hasStrategyChartAccess = hasAccess(FeatureCategory.STRATEGY_CHART , '' , FeatureName.STRATEGY_CHART_ACCESS)
    const [isSlTargetValid, setIsSlTargetValid] = useState(true);

    const basketPositions = useMemo(() => {
        return positions.filter(p => p.basketId === basketId);
    }, [positions, basketId]);

    const orderHistoryMap = useMemo(() => {
        const map: Record<string, Order> = {};
        return orderHistory.reduce((acc, order) => {
            acc[order.orderId] = order;
            return acc;
        }, map);
    }, [orderHistory]);
    const tradingAccount = useMemo(() => {
        if (mode !== 'trade') {
            return tradingAccounts.find(t => t.accountId === strategyTradingAccountId) ?? selectedTradingAccount
        }
        return selectedTradingAccount
    }, [mode, selectedTradingAccount, strategyTradingAccountId, tradingAccounts])

    useEffect(() => {
        const filteredOrders = tradeOrders.filter((order) => {
            const refId = order.referenceOrderId;
            const refOrder = refId ? orderHistoryMap[refId] : undefined;
            if (!refOrder || refOrder.status !== OrderStatus.EXECUTED) return true;
            const legs = getIcebergLegOrdersForSecurity(
                orderHistory,
                batchId,
                basketId,
                order.security
            );
            if (icebergLadderIncompleteForSecurity(legs)) return true;
            return false;
        });
        if (filteredOrders.length !== tradeOrders.length && basketPositions?.length) {
            dispatch(updateTradeOrders(filteredOrders));
        }
    }, [tradeOrders, orderHistoryMap, orderHistory, basketPositions, batchId, basketId, dispatch])
    

    const duplicateOrders = useMemo(() => {
        const eligibleOrders = tradeOrders.filter(order => !orderHistoryMap[order.referenceOrderId] || orderHistoryMap[order.referenceOrderId].batchId === batchId );
        return checkForDuplicateTradeOrders(eligibleOrders)
    }, [tradeOrders, orderHistoryMap]);
    
    const handleSaveAsTemplate = useCallback(() => {
        dispatch(setOpenStrategyTemplateDialog(true))
    }, [dispatch])

    useEffect(() => {
        dispatch(setError(
            duplicateOrders ? "You can not add multiple orders for same security" : ""
        ))
    }, [duplicateOrders])


    return  <>
                <Grid container flexGrow={1}>
                     <Box style={{ padding: '5px', width: '100%', minHeight: '200px' }}>
                        <Stack direction={"column"}>
                            <TradePanelActionBar strategyChartAccess={hasStrategyChartAccess} />
                            <Divider />
                            <TradeOrdersContainer tradingAccount={tradingAccount} />
                        </Stack>
                         {!basketAnalyzeDetails && (
                             <Stack direction="row" justifyContent="flex-end">
                                 <Button variant="text" className="reset-prices" onClick={handleSaveAsTemplate} size="small">
                                     Save as Template
                                 </Button>
                             </Stack>
                         )}
                    </Box>
                </Grid>
                <Divider />
                <Stack id={'strategy-parameters'} marginBottom={1} direction="row" gap={1} justifyContent="center">
                    <TradePanelControls />
                </Stack>
                <Divider />
                <TradeOrderAttributes hasSlAndTargetAccess={hasSlAndTargetAccess} positions={basketPositions} onSlTargetValidationChange={setIsSlTargetValid} />
                <OrderBar tradingAccount={tradingAccount} hasSlAndTargetAccess={hasSlAndTargetAccess} isSlTargetValid={isSlTargetValid}  />
            </>;
}

export default memo(TradePanel);